import { useApi } from '../../hooks/useApi';
import {
  AlertCircle,
  ArrowLeft,
  CheckCircle,
  Clock,
  Filter,
  LogOut,
  RefreshCw,
  Search,
  Settings,
  X,
  BarChart3,
  Building,
  Activity,
  Brain,
  Code,
  Tag,
  FileText
} from 'lucide-react';
import React, { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import ErrorBoundary from '../../components/ErrorBoundary';

const ErrorLogs = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const api = useApi();

  // Safe render function to prevent object rendering errors
  const safeRender = (value) => {
    if (value === null || value === undefined) return 'N/A';
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') return value;
    if (typeof value === 'object') return JSON.stringify(value, null, 2);
    return String(value);
  };
  
  const [errorLogs, setErrorLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);
  const [pagination, setPagination] = useState(null);
  
  // Filters
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [statusCodeFilter, setStatusCodeFilter] = useState('');
  const [isResolvedFilter, setIsResolvedFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedError, setSelectedError] = useState(null);
  const [jumpToPage, setJumpToPage] = useState('');

  // Redirect if not main admin
  useEffect(() => {
    if (user && user.role !== 'main_admin') {
      navigate('/dashboard');
      toast.error('You do not have permission to access this page');
    }
  }, [user, navigate]);

  // Fetch error logs
  const fetchErrorLogs = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        page,
        limit,
        ...(statusCodeFilter && { statusCode: statusCodeFilter }),
        ...(isResolvedFilter && { isResolved: isResolvedFilter }),
      };

      const response = await api.get('/api/v1/api-error-logs', { params });
      setErrorLogs(response.data.data || []);
      setPagination(response.data.pagination || null);
    } catch (error) {
      console.error('Error fetching error logs:', error);
      toast.error('Failed to fetch error logs');
    } finally {
      setLoading(false);
    }
  }, [api, page, limit, statusCodeFilter, isResolvedFilter]);

  // Fetch statistics
  const fetchStats = useCallback(async () => {
    try {
      setStatsLoading(true);
      const response = await api.get('/api/v1/api-error-logs/stats');
      setStats(response.data.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setStatsLoading(false);
    }
  }, [api]);

  useEffect(() => {
    if (user?.role === 'main_admin') {
      fetchErrorLogs();
      fetchStats();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, statusCodeFilter, isResolvedFilter]);

  // Mark as resolved
  const handleResolve = async (errorId) => {
    try {
      const notes = prompt('Enter resolution notes (optional):');
      await api.patch(`/api/v1/api-error-logs/${errorId}/resolve`, {
        notes: notes || 'Marked as resolved'
      });
      toast.success('Error marked as resolved');
      fetchErrorLogs();
      fetchStats();
    } catch (error) {
      console.error('Error resolving:', error);
      toast.error('Failed to resolve error');
    }
  };

  // Delete error log
  const handleDelete = async (errorId) => {
    if (window.confirm('Are you sure you want to delete this error log?')) {
      try {
        await api.delete(`/api/v1/api-error-logs/${errorId}`);
        toast.success('Error log deleted');
        fetchErrorLogs();
        fetchStats();
      } catch (error) {
        console.error('Error deleting:', error);
        toast.error('Failed to delete error log');
      }
    }
  };

  const getStatusCodeColor = (statusCode) => {
    if (statusCode >= 500) return 'text-red-400 bg-red-500/20';
    if (statusCode >= 400) return 'text-yellow-400 bg-yellow-500/20';
    return 'text-green-400 bg-green-500/20';
  };

  // Handle page jump
  const handlePageJump = () => {
    const targetPage = parseInt(jumpToPage);
    if (targetPage && targetPage >= 1 && targetPage <= (pagination?.pages || 1)) {
      setPage(targetPage);
      setJumpToPage('');
    } else {
      toast.error(`Please enter a valid page number between 1 and ${pagination?.pages || 1}`);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  if (user?.role !== 'main_admin') {
    return null;
  }

  return (
    <ErrorBoundary>
      <div className="h-screen flex overflow-hidden bg-black">
      {/* Desktop sidebar */}
      <div className="hidden md:flex md:flex-shrink-0">
        <div className="flex flex-col w-64">
          <div className="flex flex-col h-0 flex-1 border-r border-white/10 bg-black">
            <div className="flex-1 flex flex-col pt-5 pb-4 overflow-y-auto">
              <div className="flex items-center flex-shrink-0 px-4">
                <div className="flex items-center space-x-3">
                  <div className="h-8 w-8 flex items-center justify-center rounded-lg bg-white/10 backdrop-blur-sm border border-white/20">
                    <BarChart3 className="h-5 w-5 text-white" />
                  </div>
                  <h1 className="text-xl font-bold text-white">Main Admin</h1>
                </div>
              </div>
              <nav className="mt-5 flex-1 px-2 bg-black space-y-1">
                <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider px-3 py-2">
                  System Management
                </div>
                <button
                  onClick={() => navigate('/main-admin')}
                  className="group flex items-center px-2 py-2 text-sm font-medium rounded-md w-full text-left text-gray-400 hover:bg-white/5 hover:text-white"
                >
                  <Building className="mr-3 h-5 w-5" />
                  Organizations
                </button>
                <button
                  onClick={() => navigate('/main-admin')}
                  className="group flex items-center px-2 py-2 text-sm font-medium rounded-md w-full text-left text-gray-400 hover:bg-white/5 hover:text-white"
                >
                  <Activity className="mr-3 h-5 w-5" />
                  Scheduler Logs
                </button>
                <button
                  onClick={() => navigate('/main-admin')}
                  className="group flex items-center px-2 py-2 text-sm font-medium rounded-md w-full text-left text-gray-400 hover:bg-white/5 hover:text-white"
                >
                  <Brain className="mr-3 h-5 w-5" />
                  AI Prompts
                </button>
                <button
                  onClick={() => navigate('/main-admin')}
                  className="group flex items-center px-2 py-2 text-sm font-medium rounded-md w-full text-left text-gray-400 hover:bg-white/5 hover:text-white"
                >
                  <Code className="mr-3 h-5 w-5" />
                  System Critical Logics
                </button>
                <button
                  onClick={() => navigate('/main-admin/error-logs')}
                  className="group flex items-center px-2 py-2 text-sm font-medium rounded-md w-full text-left bg-white/10 text-white"
                >
                  <AlertCircle className="mr-3 h-5 w-5" />
                  Error Logs
                </button>
                <button
                  onClick={() => navigate('/main-admin/settings')}
                  className="group flex items-center px-2 py-2 text-sm font-medium rounded-md w-full text-left text-gray-400 hover:bg-white/5 hover:text-white"
                >
                  <Settings className="mr-3 h-5 w-5" />
                  System Settings
                </button>
                <button
                  onClick={() => navigate('/main-admin/coupons')}
                  className="group flex items-center px-2 py-2 text-sm font-medium rounded-md w-full text-left text-gray-400 hover:bg-white/5 hover:text-white"
                >
                  <Tag className="mr-3 h-5 w-5" />
                  Coupon Management
                </button>
                <button
                  onClick={() => navigate('/main-admin')}
                  className="group flex items-center px-2 py-2 text-sm font-medium rounded-md w-full text-left text-gray-400 hover:bg-white/5 hover:text-white"
                >
                  <FileText className="mr-3 h-5 w-5" />
                  Blog Management
                </button>
              </nav>
            </div>
            <div className="flex-shrink-0 flex border-t border-white/10 p-4">
              <div className="flex items-center w-full">
                <div className="flex-1">
                  <div className="text-sm font-medium text-white">{user?.name || user?.email}</div>
                  <div className="text-xs text-gray-400">{user?.role}</div>
                </div>
                <button
                  onClick={handleLogout}
                  className="p-2 rounded-md text-gray-400 hover:text-white hover:bg-white/5 focus:outline-none focus:ring-2 focus:ring-white/20"
                  title="Logout"
                >
                  <LogOut className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="flex flex-col w-0 flex-1 overflow-hidden">
        <main className="flex-1 relative z-0 overflow-y-auto focus:outline-none">
          <div className="py-6">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
              {/* Header */}
              <div className="mb-6">
                <h1 className="text-2xl font-bold text-white">API Error Logs</h1>
                <p className="text-sm text-gray-400">Monitor and manage system errors</p>
              </div>

              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Statistics Cards */}
        {!statsLoading && stats && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div className="bg-zinc-900 rounded-lg p-6 border border-zinc-800">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 text-sm">Total Errors</p>
                  <p className="text-3xl font-bold text-white mt-2">{stats.summary.total}</p>
                </div>
                <AlertCircle className="h-10 w-10 text-red-500" />
              </div>
            </div>

            <div className="bg-zinc-900 rounded-lg p-6 border border-zinc-800">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 text-sm">Unresolved</p>
                  <p className="text-3xl font-bold text-yellow-400 mt-2">{stats.summary.unresolved}</p>
                </div>
                <Clock className="h-10 w-10 text-yellow-500" />
              </div>
            </div>

            <div className="bg-zinc-900 rounded-lg p-6 border border-zinc-800">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 text-sm">Resolved</p>
                  <p className="text-3xl font-bold text-green-400 mt-2">{stats.summary.resolved}</p>
                </div>
                <CheckCircle className="h-10 w-10 text-green-500" />
              </div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="bg-zinc-900 rounded-lg p-4 border border-zinc-800 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-400 mb-2">
                <Filter className="inline h-4 w-4 mr-1" />
                Status Code
              </label>
              <select
                value={statusCodeFilter}
                onChange={(e) => setStatusCodeFilter(e.target.value)}
                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Status Codes</option>
                <option value="400">400 - Bad Request</option>
                <option value="401">401 - Unauthorized</option>
                <option value="403">403 - Forbidden</option>
                <option value="404">404 - Not Found</option>
                <option value="500">500 - Server Error</option>
                <option value="503">503 - Service Unavailable</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-400 mb-2">
                Resolution Status
              </label>
              <select
                value={isResolvedFilter}
                onChange={(e) => setIsResolvedFilter(e.target.value)}
                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Statuses</option>
                <option value="false">Unresolved</option>
                <option value="true">Resolved</option>
              </select>
            </div>

            <div className="md:col-span-2 flex items-end space-x-2">
              <button
                onClick={fetchErrorLogs}
                className="flex items-center space-x-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 !text-white rounded-lg transition-colors"
              >
                <RefreshCw className="h-4 w-4" />
                <span>Refresh</span>
              </button>
              <button
                onClick={() => {
                  setStatusCodeFilter('');
                  setIsResolvedFilter('');
                  setPage(1);
                }}
                className="flex items-center space-x-2 px-4 py-2 bg-zinc-700 hover:bg-zinc-600 text-white rounded-lg transition-colors"
              >
                <X className="h-4 w-4" />
                <span>Clear Filters</span>
              </button>
            </div>
          </div>
        </div>

        {/* Error Logs Table */}
        <div className="bg-zinc-900 rounded-lg border border-zinc-800 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1200px]">
              <thead className="bg-zinc-800">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider w-24">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider w-48">
                    Endpoint
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider w-64">
                    Error
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider w-40">
                    User
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider w-44">
                    Time
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider w-28">
                    Resolved
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider w-32">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-800">
                {loading ? (
                  <tr>
                    <td colSpan="7" className="px-6 py-12 text-center">
                      <div className="flex justify-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                      </div>
                    </td>
                  </tr>
                ) : errorLogs.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="px-6 py-12 text-center text-gray-400">
                      No error logs found
                    </td>
                  </tr>
                ) : (
                  errorLogs.map((log) => (
                    <tr key={log._id} className="hover:bg-zinc-800/50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusCodeColor(log.statusCode)}`}>
                          {log.statusCode}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-white font-mono font-semibold">{log.method}</div>
                        <div className="text-xs text-gray-400 break-words max-w-[200px] leading-tight">{log.endpoint}</div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-white break-words max-w-[250px] line-clamp-2 leading-snug">{log.error}</div>
                        {log.errorType && (
                          <div className="text-xs text-gray-500 mt-1">{log.errorType}</div>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-300 break-words max-w-[150px] line-clamp-2">
                          {log.username || log.userId?.email || 'N/A'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-400">
                          <div>{new Date(log.createdAt).toLocaleDateString()}</div>
                          <div className="text-xs">{new Date(log.createdAt).toLocaleTimeString()}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {log.isResolved ? (
                          <span className="flex items-center text-green-400">
                            <CheckCircle className="h-4 w-4 mr-1" />
                            Yes
                          </span>
                        ) : (
                          <span className="flex items-center text-yellow-400">
                            <Clock className="h-4 w-4 mr-1" />
                            No
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center space-x-2">
                          {!log.isResolved && (
                            <button
                              onClick={() => handleResolve(log._id)}
                              className="text-green-400 hover:text-green-300 transition-colors"
                              title="Mark as Resolved"
                            >
                              <CheckCircle className="h-5 w-5" />
                            </button>
                          )}
                          <button
                            onClick={() => setSelectedError(log)}
                            className="text-blue-400 hover:text-blue-300 transition-colors"
                            title="View Details"
                          >
                            <Search className="h-5 w-5" />
                          </button>
                          <button
                            onClick={() => handleDelete(log._id)}
                            className="text-red-400 hover:text-red-300 transition-colors"
                            title="Delete"
                          >
                            <X className="h-5 w-5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination && (
            <div className="bg-zinc-800 px-6 py-4 flex items-center justify-between border-t border-zinc-700">
              <button
                onClick={() => setPage(Math.max(1, page - 1))}
                disabled={page === 1}
                className="px-4 py-2 bg-zinc-700 hover:bg-zinc-600 disabled:bg-zinc-800 disabled:text-gray-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
              >
                Previous
              </button>
              <div className="flex items-center space-x-4">
                <span className="text-gray-400">
                  Page {pagination.page} of {pagination.pages}
                </span>
                <span className="text-gray-500 text-sm">
                  ({pagination.total} total errors)
                </span>
                {pagination.pages > 1 && (
                  <div className="flex items-center space-x-2 ml-4">
                    <span className="text-gray-400 text-sm">Jump to page:</span>
                    <input
                      type="number"
                      min="1"
                      max={pagination.pages}
                      value={jumpToPage}
                      onChange={(e) => setJumpToPage(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          handlePageJump();
                        }
                      }}
                      placeholder="Page"
                      className="w-20 bg-zinc-700 border border-zinc-600 rounded-lg px-3 py-1 text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <button
                      onClick={handlePageJump}
                      className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-lg transition-colors"
                    >
                      Go
                    </button>
                  </div>
                )}
              </div>
              <button
                onClick={() => setPage(page + 1)}
                disabled={page >= pagination.pages}
                className="px-4 py-2 bg-zinc-700 hover:bg-zinc-600 disabled:bg-zinc-800 disabled:text-gray-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
              >
                Next
              </button>
            </div>
          )}
        </div>

        {/* Detail Modal */}
        {selectedError && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4 overflow-y-auto">
            <div className="bg-zinc-900 rounded-lg max-w-5xl w-full my-8 border border-zinc-800">
              <div className="sticky top-0 bg-zinc-900 border-b border-zinc-800 px-6 py-4 flex items-center justify-between rounded-t-lg">
                <div className="flex items-center space-x-3">
                  <AlertCircle className="h-6 w-6 text-red-500" />
                  <h3 className="text-xl font-bold text-white">Error Log Details</h3>
                </div>
                <button
                  onClick={() => setSelectedError(null)}
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
              
              <div className="p-6 space-y-6 max-h-[80vh] overflow-y-auto">
                {/* Basic Info Section */}
                <div className="bg-zinc-800/50 rounded-lg p-4 space-y-3">
                  <h4 className="text-lg font-semibold text-white mb-3 flex items-center">
                    <AlertCircle className="h-5 w-5 mr-2 text-red-400" />
                    Basic Information
                  </h4>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs font-medium text-gray-400 uppercase tracking-wide">Status Code</label>
                      <p className="text-white mt-1">
                        <span className={`px-3 py-1 rounded text-sm font-semibold ${getStatusCodeColor(selectedError.statusCode)}`}>
                          {selectedError.statusCode}
                        </span>
                      </p>
                    </div>
                    
                    <div>
                      <label className="text-xs font-medium text-gray-400 uppercase tracking-wide">Error Type</label>
                      <p className="text-white mt-1 break-words">{selectedError.errorType || 'N/A'}</p>
                    </div>
                    
                    <div className="md:col-span-2">
                      <label className="text-xs font-medium text-gray-400 uppercase tracking-wide">Endpoint</label>
                      <p className="text-white mt-1 font-mono text-sm break-all bg-zinc-800 p-2 rounded">
                        <span className="text-blue-400 font-bold">{selectedError.method}</span> {selectedError.endpoint}
                      </p>
                    </div>
                    
                    <div className="md:col-span-2">
                      <label className="text-xs font-medium text-gray-400 uppercase tracking-wide">Error Message</label>
                      <p className="text-red-300 mt-1 break-words bg-red-900/20 p-3 rounded border border-red-800/30">
                        {selectedError.error}
                      </p>
                    </div>
                    
                    <div>
                      <label className="text-xs font-medium text-gray-400 uppercase tracking-wide">Timestamp</label>
                      <p className="text-white mt-1">{new Date(selectedError.createdAt).toLocaleString()}</p>
                    </div>
                    
                    <div>
                      <label className="text-xs font-medium text-gray-400 uppercase tracking-wide">Resolution Status</label>
                      <p className="mt-1">
                        {selectedError.isResolved ? (
                          <span className="inline-flex items-center text-green-400">
                            <CheckCircle className="h-4 w-4 mr-1" />
                            Resolved
                          </span>
                        ) : (
                          <span className="inline-flex items-center text-yellow-400">
                            <Clock className="h-4 w-4 mr-1" />
                            Unresolved
                          </span>
                        )}
                      </p>
                    </div>
                  </div>
                </div>

                {/* User Information Section */}
                <div className="bg-zinc-800/50 rounded-lg p-4 space-y-3">
                  <h4 className="text-lg font-semibold text-white mb-3 flex items-center">
                    <Search className="h-5 w-5 mr-2 text-blue-400" />
                    User Information
                  </h4>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs font-medium text-gray-400 uppercase tracking-wide">Username</label>
                      <p className="text-white mt-1 break-words">{selectedError.username || 'N/A'}</p>
                    </div>
                    
                    <div>
                      <label className="text-xs font-medium text-gray-400 uppercase tracking-wide">User ID</label>
                      <p className="text-white mt-1 break-all font-mono text-sm">
                        {selectedError.userId?._id || selectedError.userId || 'N/A'}
                      </p>
                    </div>
                    
                    {selectedError.userId?.email && (
                      <div>
                        <label className="text-xs font-medium text-gray-400 uppercase tracking-wide">Email</label>
                        <p className="text-white mt-1 break-words">{selectedError.userId.email}</p>
                      </div>
                    )}
                    
                    {selectedError.userId?.firstName && (
                      <div>
                        <label className="text-xs font-medium text-gray-400 uppercase tracking-wide">Name</label>
                        <p className="text-white mt-1">
                          {selectedError.userId.firstName} {selectedError.userId.lastName || ''}
                        </p>
                      </div>
                    )}
                    
                  </div>
                </div>

                {/* System/App Information from userData */}
                {selectedError.userData && (selectedError.userData.systemType || selectedError.userData.appVersion) && (
                  <div className="bg-zinc-800/50 rounded-lg p-4 space-y-3">
                    <h4 className="text-lg font-semibold text-white mb-3 flex items-center">
                      <Settings className="h-5 w-5 mr-2 text-cyan-400" />
                      System & Application Information
                    </h4>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {selectedError.userData.systemType && (
                        <div>
                          <label className="text-xs font-medium text-gray-400 uppercase tracking-wide">System Type</label>
                          <p className="text-white mt-1 font-mono">{selectedError.userData.systemType}</p>
                        </div>
                      )}
                      
                      {selectedError.userData.appVersion && (
                        <div>
                          <label className="text-xs font-medium text-gray-400 uppercase tracking-wide">App Version</label>
                          <p className="text-white mt-1 font-mono">{selectedError.userData.appVersion}</p>
                        </div>
                      )}
                      
                      {selectedError.userData.nodeVersion && (
                        <div>
                          <label className="text-xs font-medium text-gray-400 uppercase tracking-wide">Node Version</label>
                          <p className="text-white mt-1 font-mono">{selectedError.userData.nodeVersion}</p>
                        </div>
                      )}
                      
                      {selectedError.userData.electronVersion && (
                        <div>
                          <label className="text-xs font-medium text-gray-400 uppercase tracking-wide">Electron Version</label>
                          <p className="text-white mt-1 font-mono">{selectedError.userData.electronVersion}</p>
                        </div>
                      )}
                      
                      {selectedError.userData.time && (
                        <div>
                          <label className="text-xs font-medium text-gray-400 uppercase tracking-wide">Client Time</label>
                          <p className="text-white mt-1">{new Date(selectedError.userData.time).toLocaleString()}</p>
                        </div>
                      )}
                      
                      {selectedError.userData.timeout && (
                        <div>
                          <label className="text-xs font-medium text-gray-400 uppercase tracking-wide">Request Timeout</label>
                          <p className="text-white mt-1">{selectedError.userData.timeout}ms</p>
                        </div>
                      )}
                      
                      {selectedError.userData.hasAuth !== undefined && (
                        <div>
                          <label className="text-xs font-medium text-gray-400 uppercase tracking-wide">Has Authentication</label>
                          <p className="text-white mt-1">
                            <span className={`px-2 py-1 rounded text-xs font-semibold ${selectedError.userData.hasAuth ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
                              {selectedError.userData.hasAuth ? 'Yes' : 'No'}
                            </span>
                          </p>
                        </div>
                      )}
                      
                      {selectedError.userData.requestUrl && (
                        <div className="md:col-span-2">
                          <label className="text-xs font-medium text-gray-400 uppercase tracking-wide">Request URL</label>
                          <p className="text-white mt-1 break-all font-mono text-sm bg-zinc-800 p-2 rounded">
                            <span className="text-cyan-400 font-bold">{selectedError.userData.requestMethod || 'N/A'}</span> {selectedError.userData.requestUrl}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Error Details from userData */}
                {selectedError.userData?.errorDetails && (
                  <div className="bg-zinc-800/50 rounded-lg p-4 space-y-4">
                    <h4 className="text-lg font-semibold text-white mb-3 flex items-center">
                      <AlertCircle className="h-5 w-5 mr-2 text-orange-400" />
                      Detailed Error Information
                    </h4>
                    
                    {/* Response Data from errorDetails */}
                    {selectedError.userData.errorDetails.responseData && (
                      <div>
                        <label className="text-xs font-medium text-gray-400 uppercase tracking-wide">Response Data</label>
                        <div className="mt-2 bg-red-900/20 p-4 rounded-lg border border-red-800/30 space-y-2">
                          {selectedError.userData.errorDetails.responseData.error?.message && (
                            <div>
                              <p className="text-xs text-red-400 font-semibold">Error Message:</p>
                              <p className="text-red-200 mt-1">{selectedError.userData.errorDetails.responseData.error.message}</p>
                            </div>
                          )}
                          
                          {selectedError.userData.errorDetails.responseData.error?.details && Array.isArray(selectedError.userData.errorDetails.responseData.error.details) && (
                            <div>
                              <p className="text-xs text-red-400 font-semibold">Validation Errors:</p>
                              <ul className="list-disc list-inside mt-1 text-red-200">
                                {selectedError.userData.errorDetails.responseData.error.details.map((detail, idx) => (
                                  <li key={idx} className="text-sm">{detail}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                          
                          {selectedError.userData.errorDetails.responseData.error?.details && !Array.isArray(selectedError.userData.errorDetails.responseData.error.details) && (
                            <div>
                              <p className="text-xs text-red-400 font-semibold">Error Details:</p>
                              <p className="text-red-200 mt-1 text-sm">{safeRender(selectedError.userData.errorDetails.responseData.error.details)}</p>
                            </div>
                          )}
                          
                          {selectedError.userData.errorDetails.responseData.error?.stack && (
                            <div>
                              <p className="text-xs text-red-400 font-semibold">Error Stack:</p>
                              <pre className="text-red-200 mt-1 text-xs font-mono whitespace-pre-wrap break-words bg-red-900/30 p-3 rounded max-h-60 overflow-y-auto">
                                {selectedError.userData.errorDetails.responseData.error.stack}
                              </pre>
                            </div>
                          )}
                          
                          {selectedError.userData.errorDetails.responseData.timestamp && (
                            <div>
                              <p className="text-xs text-red-400 font-semibold">Response Timestamp:</p>
                              <p className="text-red-200 mt-1">{new Date(selectedError.userData.errorDetails.responseData.timestamp).toLocaleString()}</p>
                            </div>
                          )}
                          
                          {selectedError.userData.errorDetails.responseData.path && (
                            <div>
                              <p className="text-xs text-red-400 font-semibold">Error Path:</p>
                              <p className="text-red-200 mt-1 font-mono">{selectedError.userData.errorDetails.responseData.path}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                    
                    {/* Response Status */}
                    {selectedError.userData.errorDetails.responseStatus && (
                      <div>
                        <label className="text-xs font-medium text-gray-400 uppercase tracking-wide">Response Status</label>
                        <p className="text-white mt-1">
                          <span className={`px-3 py-1 rounded text-sm font-semibold ${getStatusCodeColor(selectedError.userData.errorDetails.responseStatus)}`}>
                            {selectedError.userData.errorDetails.responseStatus}
                          </span>
                        </p>
                      </div>
                    )}
                    
                    {/* Response Headers */}
                    {selectedError.userData.errorDetails.responseHeaders && Object.keys(selectedError.userData.errorDetails.responseHeaders).length > 0 && (
                      <div>
                        <label className="text-xs font-medium text-gray-400 uppercase tracking-wide">Response Headers</label>
                        <pre className="text-white mt-2 bg-zinc-800 p-4 rounded-lg overflow-x-auto text-xs border border-zinc-700 max-h-60 overflow-y-auto">
                          {safeRender(selectedError.userData.errorDetails.responseHeaders)}
                        </pre>
                      </div>
                    )}
                    
                    {/* Request Config */}
                    {selectedError.userData.errorDetails.requestConfig && (
                      <div>
                        <label className="text-xs font-medium text-gray-400 uppercase tracking-wide">Request Configuration</label>
                        <div className="mt-2 bg-zinc-800 p-4 rounded-lg border border-zinc-700 space-y-2">
                          {selectedError.userData.errorDetails.requestConfig.headers && (
                            <div>
                              <p className="text-xs text-gray-400 font-semibold">Headers:</p>
                              <div className="mt-1 space-y-1">
                                {Object.entries(selectedError.userData.errorDetails.requestConfig.headers).map(([key, value]) => (
                                  <div key={key} className="flex items-start text-xs">
                                    <span className="text-cyan-400 font-mono min-w-[120px]">{key}:</span>
                                    <span className="text-white font-mono break-all ml-2">
                                      {key.toLowerCase().includes('authorization') && typeof value === 'string' && value.startsWith('Bearer ') 
                                        ? `Bearer ${value.substring(7, 20)}...${value.substring(value.length - 10)}`
                                        : safeRender(value)}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                          
                          {selectedError.userData.errorDetails.requestConfig.timeout && (
                            <div>
                              <p className="text-xs text-gray-400 font-semibold">Timeout:</p>
                              <p className="text-white mt-1 text-xs">{selectedError.userData.errorDetails.requestConfig.timeout}ms</p>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Raw User Data (fallback for any additional data) */}
                {selectedError.userData && Object.keys(selectedError.userData).length > 0 && (
                  <div className="bg-zinc-800/50 rounded-lg p-4 space-y-3">
                    <h4 className="text-lg font-semibold text-white mb-3 flex items-center">
                      <Search className="h-5 w-5 mr-2 text-gray-400" />
                      Complete User Data (Raw)
                    </h4>
                    <pre className="text-white bg-zinc-800 p-4 rounded-lg overflow-x-auto text-xs border border-zinc-700 max-h-80 overflow-y-auto">
                      {safeRender(selectedError.userData)}
                    </pre>
                  </div>
                )}

                {/* Organization Information */}
                {selectedError.organizationId && (
                  <div className="bg-zinc-800/50 rounded-lg p-4 space-y-3">
                    <h4 className="text-lg font-semibold text-white mb-3 flex items-center">
                      <Settings className="h-5 w-5 mr-2 text-purple-400" />
                      Organization Information
                    </h4>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="text-xs font-medium text-gray-400 uppercase tracking-wide">Organization ID</label>
                        <p className="text-white mt-1 break-all font-mono text-sm">
                          {selectedError.organizationId._id || selectedError.organizationId}
                        </p>
                      </div>
                      
                      {selectedError.organizationId.name && (
                        <div>
                          <label className="text-xs font-medium text-gray-400 uppercase tracking-wide">Organization Name</label>
                          <p className="text-white mt-1">{selectedError.organizationId.name}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Resolution Information */}
                {selectedError.isResolved && selectedError.notes && (
                  <div className="bg-green-900/20 border border-green-800/30 rounded-lg p-4 space-y-3">
                    <h4 className="text-lg font-semibold text-green-300 mb-3 flex items-center">
                      <CheckCircle className="h-5 w-5 mr-2" />
                      Resolution Information
                    </h4>
                    
                    <div>
                      <label className="text-xs font-medium text-green-400 uppercase tracking-wide">Resolution Notes</label>
                      <p className="text-white mt-1 break-words bg-zinc-800 p-3 rounded">{selectedError.notes}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
    </ErrorBoundary>
  );
};

export default ErrorLogs;

